<template>
  <DashboardHeaderContainer class="actions-container">
    <div class="actions-content">
      <div class="non-buttons-block">
        <Select
          v-model="selectedReportKey"
          class="pre-selected-report"
          label="Saved Reports"
          name="report-select"
          :options="reportKeys"
          @input="setReportOptions"
        ></Select>
        <div
          v-if="isUpdated"
          class="update-message"
        >
          <RefreshIcon></RefreshIcon>
          <span>
            You've made changes to your report. Update to view the latest.
          </span>
        </div>
      </div>
      <div class="buttons-block">
        <PrimaryButton
          class="actions-button"
          :disabled="isUpdateDisabled"
          @click="$emit('updateReport')"
        >
          <template v-slot:icon>
            <RefreshIcon size="small"></RefreshIcon>
          </template>
          Update
        </PrimaryButton>
        <CenterAlignTooltip>
          <IconButton
            class="actions-button"
            @click="$emit('toggleSaveReportPopUp')"
            :disabled="!this.isReportRunnable"
          >
            <SaveIcon></SaveIcon>
          </IconButton>
          <template v-slot:content>Save a New Report</template>
        </CenterAlignTooltip>
        <CenterAlignTooltip>
          <IconButton
            class="actions-button"
            @click="$emit('openScheduleReportSlideOut')"
            :disabled="!this.isReportRunnable"
          >
            <ScheduleIcon></ScheduleIcon>
          </IconButton>
          <template v-slot:content>Schedule a New Report</template>
        </CenterAlignTooltip>
        <CenterAlignTooltip>
          <IconButton class="actions-button timeseries" @click="$emit('toggleTimeSeries')">
            <LineChartIcon></LineChartIcon>
          </IconButton>
          <template v-slot:content>Show Timeseries</template>
        </CenterAlignTooltip>
        <CenterAlignTooltip>
          <IconButton class="actions-button" @click="copyCurrentUrl">
            <LinkIcon></LinkIcon>
          </IconButton>
          <template v-slot:content>{{ urlCopyText }}</template>
        </CenterAlignTooltip>
        <CenterAlignTooltip>
          <IconButton
            class="actions-button"
            :disabled="!isResultSetDownloadable"
            @click="$emit('downloadClick')"
          >
            <ExportIcon></ExportIcon>
          </IconButton>
          <template v-slot:content>Export as CSV</template>
        </CenterAlignTooltip>
        <CenterAlignTooltip class="filter-icon-tooltip">
          <FilterIcon
            class="filter-icon"
            :color="filterIconColor"
            @click.native="$emit('toggleFilters', true)"
          ></FilterIcon>
          <template v-slot:content>Show/Hide Filters</template>
        </CenterAlignTooltip>
      </div>
    </div>
  </DashboardHeaderContainer>
</template>
<script>
import {
  CenterAlignTooltip,
  ExportIcon,
  FilterIcon,
  IconButton,
  LineChartIcon,
  LinkIcon,
  PrimaryButton,
  RefreshIcon,
  Select,
  SaveIcon,
  ScheduleIcon,
} from '@mikmak/components';
import DashboardHeaderContainer from '../Common/DashboardHeaderContainer.vue';

export default {
  props: {
    reports: {
      type: Array,
      required: true,
    },
    resultSet: {
      type: Object,
      default: () => ({
        columns: [],
        rows: [],
      }),
    },
    isReportRunnable: {
      type: Boolean,
      default: false,
    },
    isUpdated: {
      type: Boolean,
    },
    activeFilter: {
      type: Boolean,
    },
  },
  components: {
    CenterAlignTooltip,
    DashboardHeaderContainer,
    ExportIcon,
    FilterIcon,
    IconButton,
    LineChartIcon,
    LinkIcon,
    PrimaryButton,
    RefreshIcon,
    Select,
    SaveIcon,
    ScheduleIcon,
  },
  data() {
    return {
      selectedReportKey: '',
      isCopied: false,
    };
  },
  computed: {
    reportKeys() {
      return this.reports.map(({ key }) => {
        return {
          key,
          value: key,
        };
      });
    },
    isUpdateDisabled() {
      return !this.isReportRunnable || !this.isUpdated;
    },
    isResultSetDownloadable() {
      return this.resultSet
        && this.resultSet.rows
        && this.resultSet.rows.length
        && this.resultSet.columns
        && this.resultSet.columns.length;
    },
    urlCopyText() {
      return this.isCopied ? 'Copied!' : 'Copy Share Link';
    },
    filterIconColor() {
      return this.activeFilter ? '#4369F4' : undefined;
    },
  },
  methods: {
    setReportOptions(reportKey) {
      const selectedReport = this.reports.find(({ key }) => key === reportKey);

      this.$emit('reportSelect', selectedReport);
    },
    copyCurrentUrl() {
      const invisibleInput = document.createElement('input');
      const text = window.location.href;

      document.body.appendChild(invisibleInput);
      invisibleInput.value = text;
      invisibleInput.select();
      document.execCommand('copy');
      document.body.removeChild(invisibleInput);

      this.isCopied = true;

      setTimeout(() => {
        this.isCopied = false;
      }, 2000);
    },
  },
  watch: {
    isUpdated(val) {
      if (val) this.selectedReportKey = '';
    },
  },
};
</script>
<style scoped>
.actions-container {
  display: flex;
  justify-content: center;
}

.actions-content {
  height: 100%;
  width: 1360px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.non-buttons-block {
  display: flex;
  align-items: center;
  padding-left: 24px;
}

.pre-selected-report.wrapper {
  width: 350px;
}

.update-message {
  display: flex;
  align-items: center;
  height: 44px;
  color: #223448;
  background-color: #EDF3F7;
  border-radius: 4px;
  padding-left: 10px;
  padding-right: 10px;
}

.update-message span {
  font-family: Lato;
  font-size: 16px;
  padding-left: 5px;
  white-space: nowrap;
}

.buttons-block {
  display: flex;
  align-items: center;
  padding-right: 24px;
}

.actions-button {
  margin-left: 4px;
  margin-right: 4px;
}

.main-wrapper ::v-deep .tooltip-wrapper {
  z-index: 3;
}

.buttons-block .filter-icon-tooltip {
  cursor: pointer;
  margin-left: 30px;
  margin-right: 12px;
}

.buttons-block .icon-button-wrapper.focused {
  color: #223448;
  background-color: #FFFFFF;
  border: 1.5px solid #C1CCD4;
}

.buttons-block .disabled {
  pointer-events: none;
}
</style>
